import {
  DealerMapGuard,
  EquipmentEmptyStateLayout,
  EquipmentLoadingGuard,
  MenuAndContent,
} from 'app/components/layouts';
import useFlag from 'app/hooks/useFlag';
import { AboutPage } from 'app/pages/AboutPage';
import AccessSharingPage from 'app/pages/AccessSharingPage';
import EquipmentAndFleets from 'app/pages/AccessSharingPage/EquipmentAndFleets';
import PendingInvitations from 'app/pages/AccessSharingPage/PendingInvitations';
import SharedWith from 'app/pages/AccessSharingPage/SharedWith';
import {
  CreateFleetPage,
  FleetAccessAssignmentPage,
  MachineAccessAssignmentPage,
  MachineDetailsPage,
  ShareAsPartOfFleetPage,
  ShareEquipmentPage,
  ShareFleetPage,
  ShareIndividualEquipmentPage,
} from 'app/pages/AccessSharingPage/pages';
import { FleetDetailsPage } from 'app/pages/AccessSharingPage/pages/FleetDetailsPage';
import { DealerList } from 'app/pages/Dealers/DealerList';
import { DealerMap } from 'app/pages/Dealers/DealerMap';
import { EquipmentContentIndex } from 'app/pages/Equipment/ContentIndex';
import { EquipmentContent } from 'app/pages/Equipment/EquipmentContent';
import { EquipmentDetailsFleetDetailsPage } from 'app/pages/Equipment/EquipmentDetailsFleetDetailsPage';
import { EquipmentDetailsMachineDetailsPage } from 'app/pages/Equipment/EquipmentDetailsMachineDetailsPage';
import { EquipmentList } from 'app/pages/Equipment/EquipmentList';
import { Geofence } from 'app/pages/Equipment/Geofence';
import { GeofencesMenu } from 'app/pages/Equipment/Geofence/GeofencesMenu';
import { LegalTermsPage } from 'app/pages/LegalTermsPage';
import { MaintenanceSchedules } from 'app/pages/MaintenanceSchedulePage';
import { MaintenanceDetails } from 'app/pages/MaintenanceSchedulePage/Details';
import { ManageSubscriptionPlansPage } from 'app/pages/ManageSubscriptionPlansPage';
import { GuidesPage } from 'app/pages/ResourcesPage/GuidesPage';
import { ManualViewer } from 'app/pages/ResourcesPage/ManualViewer';
import { ResourcesFaultCodesPage } from 'app/pages/ResourcesPage/ResourcesFaultCodesPage';
import { VideoResourcesPage } from 'app/pages/ResourcesPage/VideoResourcesPage';
import { SettingsPage } from 'app/pages/SettingsPage';
import { TrackMaintanenceContent } from 'app/pages/TrackMaintenencePage';
import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { FeatureName } from 'types/entities/MobileSettings';

export default function PrivateRoutes() {
  const isAccessSharingPageEnabled = useFlag(FeatureName.ACCESS_SHARING);
  const isisSubscriptionsPageEnabled = useFlag(FeatureName.SUBSCRIPTIONS);

  return (
    <>
      <Route
        element={<EquipmentEmptyStateLayout submenu={<EquipmentList />} />}
      >
        <Route
          path="/equipment/"
          element={
            <EquipmentContentIndex>
              <EquipmentContent />
            </EquipmentContentIndex>
          }
        />
        <Route
          path="/equipment/:id/"
          element={
            <EquipmentContentIndex>
              <EquipmentContent />
            </EquipmentContentIndex>
          }
        />
        {isAccessSharingPageEnabled && (
          <>
            <Route
              path="/equipment/:id/manage-users"
              element={
                <EquipmentContentIndex>
                  <EquipmentDetailsMachineDetailsPage />
                </EquipmentContentIndex>
              }
            />

            <Route
              path="/equipment/:id/fleet"
              element={
                <EquipmentContentIndex>
                  <EquipmentDetailsFleetDetailsPage />
                </EquipmentContentIndex>
              }
            />
          </>
        )}

        <Route
          path="/equipment/:id/guides"
          element={
            <EquipmentContentIndex>
              <GuidesPage />
            </EquipmentContentIndex>
          }
        />

        <Route
          path="/equipment/:id/faultCodes"
          element={
            <EquipmentContentIndex>
              <ResourcesFaultCodesPage />
            </EquipmentContentIndex>
          }
        />

        <Route
          path="/equipment/:id/manual"
          element={
            <EquipmentContentIndex>
              <ManualViewer />
            </EquipmentContentIndex>
          }
        />

        <Route
          path="/equipment/:id/videoResources"
          element={
            <EquipmentContentIndex>
              <VideoResourcesPage />
            </EquipmentContentIndex>
          }
        />

        <Route
          path="/equipment/:id/trackMaintenance"
          element={
            <EquipmentContentIndex>
              <TrackMaintanenceContent />
            </EquipmentContentIndex>
          }
        />

        <Route
          path="/equipment/:id/maintenanceSchedule"
          element={
            <EquipmentContentIndex>
              <MaintenanceSchedules />
            </EquipmentContentIndex>
          }
        />

        <Route
          path="/equipment/:id/maintenanceSchedule/:interval"
          element={
            <EquipmentContentIndex>
              <MaintenanceDetails />
            </EquipmentContentIndex>
          }
        />
      </Route>

      <Route
        element={<EquipmentEmptyStateLayout submenu={<GeofencesMenu />} />}
      >
        <Route path="/equipment/geofences" element={<Geofence />} />
      </Route>

      {isAccessSharingPageEnabled && (
        <>
          <Route element={<EquipmentLoadingGuard />}>
            <Route
              path="/equipment/:id/manage-users/add-new-user"
              element={<MachineAccessAssignmentPage />}
            />

            <Route path="/access">
              <Route
                index
                element={<Navigate to="equipment-and-fleets" replace />}
              />

              <Route element={<AccessSharingPage />}>
                <Route
                  path="equipment-and-fleets"
                  element={<EquipmentAndFleets />}
                />
                <Route path="shared-with" element={<SharedWith />} />
                <Route
                  path="pending-invitations"
                  element={<PendingInvitations />}
                />
              </Route>

              <Route path="details/:id" element={<MachineDetailsPage />} />
              <Route path="share-equipment" element={<ShareEquipmentPage />} />
              <Route
                path="equipment/:id/share"
                element={<ShareIndividualEquipmentPage />}
              />
              <Route path="create-fleet" element={<CreateFleetPage />} />
              <Route path="share-fleet" element={<ShareFleetPage />} />
              <Route
                path="equipment/:id/share-fleet"
                element={<ShareAsPartOfFleetPage />}
              />

              <Route
                path="shared-equipment/:id/add-new-user"
                element={<MachineAccessAssignmentPage />}
              />

              <Route
                path="shared-fleet/:id/add-new-user"
                element={<FleetAccessAssignmentPage />}
              />
            </Route>
          </Route>

          {/* It's placed separately due to a different mobile layout approach. Can be optimized later */}
          <Route
            path="/access"
            element={<EquipmentLoadingGuard useTheEntireSpace />}
          >
            <Route
              path="shared-equipment/:id"
              element={<MachineDetailsPage />}
            />

            <Route path="shared-fleet/:id" element={<FleetDetailsPage />} />
          </Route>
        </>
      )}

      {isisSubscriptionsPageEnabled && (
        <>
          <Route element={<EquipmentLoadingGuard />}>
            <Route
              path="/subscriptions"
              element={<ManageSubscriptionPlansPage />}
            />
          </Route>

          <Route
            element={<EquipmentEmptyStateLayout submenu={<EquipmentList />} />}
          >
            <Route
              path="/equipment/:id/subscriptionSuccess"
              element={
                <EquipmentContentIndex>
                  <EquipmentContent
                    isSubPlanChangedSuccessfullyDialogVisibility
                  />
                </EquipmentContentIndex>
              }
            />
          </Route>
        </>
      )}

      <Route element={<DealerMapGuard submenu={<DealerList />} />}>
        <Route path="/dealers" element={<DealerMap />} />
      </Route>

      <Route
        path="/about"
        element={
          <MenuAndContent>
            <AboutPage />
          </MenuAndContent>
        }
      />

      <Route
        path="/legalTerms"
        element={
          <MenuAndContent>
            <LegalTermsPage />
          </MenuAndContent>
        }
      />

      <Route
        path="/settings"
        element={
          <MenuAndContent>
            <SettingsPage />
          </MenuAndContent>
        }
      />
    </>
  );
}
